import React, {Component} from "react";
import {
	Box,
	CircularProgress,
	IconButton,
	InputAdornment,
	List,
	ListItemButton,
	ListItemText,
	Stack,
	TextField,
	Tooltip,
	Typography,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import RefreshIcon from "@mui/icons-material/Refresh";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import SmartToyOutlinedIcon from "@mui/icons-material/SmartToyOutlined";
import {aclPermissions, hasPermissions} from "../const/ACLPermissions";
import AiChatApp from "../component/gammaviewer/sidebarTop_apps/aiChatApp";
import {
	fetchSlideAiChats,
	gammaUrlForMapState,
	renameSlideAiChat,
} from "../component/gammaviewer/utils/slide_ai_utils";
import {getStateAwareUrl, isSuperuser} from "../utils/utils";
import {error_manager} from "../helper/error_manager";

const SIDEBAR_W = 340;

const chatRowKey = (chat) => `${chat.slide_id}:${chat.chat_id || "legacy"}`;
const chatDisplayTitle = (chat) => chat?.title || chat?.morphle_id || String(chat?.slide_id || "").slice(0, 8);

const formatWhen = (iso) => {
	if (!iso) return "";
	try {
		const d = new Date(iso);
		if (Number.isNaN(d.getTime())) return "";
		return d.toLocaleString(undefined, {
			month: "short", day: "numeric", hour: "2-digit", minute: "2-digit",
		});
	} catch (e) {
		return "";
	}
};

/**
 * Morpho Chat history — WhatsApp/ChatGPT-style list + full-screen thread.
 * Per-user chats only (ACL: ai_chat_app).
 */
class MorphoChatDashboard extends Component {
	constructor(props) {
		super(props);
		this.state = {
			chats: [],
			loading: true,
			search: "",
			selectedSlideId: null,
			selectedChatId: null,
			selectedMeta: null,
			editingKey: null,
			editingTitle: "",
		};
		this.searchTimer = null;
	}

	componentDidMount() {
		this.loadChats();
		const params = new URLSearchParams(window.location.search || "");
		const slideId = params.get("slide_id");
		const chatId = params.get("chat_id");
		if (slideId) {
			this.selectChat({slide_id: slideId, chat_id: chatId || null});
		}
	}

	componentWillUnmount() {
		if (this.searchTimer) clearTimeout(this.searchTimer);
	}

	loadChats = (q = this.state.search) => {
		if (!hasPermissions([aclPermissions.ai_chat_app])) {
			this.setState({loading: false, chats: []});
			return;
		}
		this.setState({loading: true});
		fetchSlideAiChats({
			q: q || "",
			limit: 200,
			includeArchived: Boolean(isSuperuser()),
		})
			.then((data) => {
				const chats = data.chats || [];
				this.setState((prev) => {
					let selectedSlideId = prev.selectedSlideId;
					let selectedChatId = prev.selectedChatId;
					let selectedMeta = prev.selectedMeta;
					if (selectedSlideId) {
						selectedMeta = chats.find((c) => (
							String(c.slide_id) === String(selectedSlideId)
							&& (!selectedChatId || String(c.chat_id) === String(selectedChatId))
						)) || chats.find((c) => String(c.slide_id) === String(selectedSlideId)) || selectedMeta;
					}
					return {chats, loading: false, selectedMeta};
				});
			})
			.catch((err) => {
				this.setState({loading: false});
				error_manager("Failed to load Morpho Chat history", err);
			});
	};

	onSearchChange = (e) => {
		const search = e.target.value;
		this.setState({search});
		if (this.searchTimer) clearTimeout(this.searchTimer);
		this.searchTimer = setTimeout(() => this.loadChats(search), 350);
	};

	selectChat = (chat) => {
		if (!chat?.slide_id) return;
		this.setState({
			selectedSlideId: chat.slide_id,
			selectedChatId: chat.chat_id || null,
			selectedMeta: chat,
		});
		const url = new URL(window.location.href);
		url.searchParams.set("slide_id", chat.slide_id);
		if (chat.chat_id) url.searchParams.set("chat_id", chat.chat_id);
		else url.searchParams.delete("chat_id");
		window.history.replaceState({}, "", url.pathname + url.search);
	};

	thumbSrc = (chat) => {
		const t = chat?.thumb;
		if (!t) return null;
		if (t.url_safe) {
			if (/^https?:\/\//i.test(t.url_safe) || t.url_safe.startsWith("blob:")) return t.url_safe;
			return window.location.origin + getStateAwareUrl("") + t.url_safe;
		}
		if (t.rel_path && chat.slide_id) {
			return getStateAwareUrl(
				`/api/slide_ai/attachment/?slide_id=${encodeURIComponent(chat.slide_id)}&rel_path=${encodeURIComponent(t.rel_path)}`
			);
		}
		return null;
	};

	startRename = (chat) => {
		this.setState({editingKey: chatRowKey(chat), editingTitle: chatDisplayTitle(chat)});
	};

	cancelRename = () => {
		this.setState({editingKey: null, editingTitle: ""});
	};

	commitRename = (chat) => {
		if (this.state.editingKey !== chatRowKey(chat)) return;
		const title = this.state.editingTitle.trim();
		this.cancelRename();
		if (!title || title === chatDisplayTitle(chat) || !chat.chat_id) return;
		const applyTitle = (newTitle) => this.setState((prev) => {
			const update = (c) => (chatRowKey(c) === chatRowKey(chat) ? {...c, title: newTitle} : c);
			return {
				chats: prev.chats.map(update),
				selectedMeta: prev.selectedMeta ? update(prev.selectedMeta) : prev.selectedMeta,
			};
		});
		const previous = chat.title;
		applyTitle(title);
		renameSlideAiChat(chat.slide_id, chat.chat_id, title)
			.then((data) => {
				if (data?.title) applyTitle(data.title);
			})
			.catch((err) => {
				applyTitle(previous);
				error_manager("Failed to rename chat", err);
			});
	};

	onEmbeddedChatRenamed = (chatId, title) => {
		const update = (c) => (String(c.chat_id) === String(chatId) ? {...c, title} : c);
		this.setState((prev) => ({
			chats: prev.chats.map(update),
			selectedMeta: prev.selectedMeta ? update(prev.selectedMeta) : prev.selectedMeta,
		}));
	};

	openInViewer = (chat) => {
		const url = gammaUrlForMapState(chat.slide_id, chat?.thumb?.map_state, {openChat: true});
		if (url) window.open(url, "_blank", "noopener");
	};

	render() {
		if (!hasPermissions([aclPermissions.ai_chat_app])) {
			return (
				<Box sx={{p: 3}}>
					<Typography>No permission for Morpho Chat.</Typography>
				</Box>
			);
		}
		const {chats, loading, search, selectedSlideId, selectedChatId, selectedMeta} = this.state;
		const selected = selectedMeta || chats.find((c) => (
			String(c.slide_id) === String(selectedSlideId)
			&& (!selectedChatId || String(c.chat_id || "") === String(selectedChatId || ""))
		)) || chats.find((c) => String(c.slide_id) === String(selectedSlideId));

		return (
			<Box
				sx={{
					display: "flex",
					height: "calc(100vh - 0px)",
					minHeight: 480,
					bgcolor: "#121218",
					color: "#fff",
					overflow: "hidden",
				}}
			>
				{/* Left: history list */}
				<Box
					sx={{
						width: SIDEBAR_W,
						flexShrink: 0,
						borderRight: "1px solid rgba(255,255,255,0.12)",
						display: "flex",
						flexDirection: "column",
						bgcolor: "#18181f",
					}}
				>
					<Box sx={{px: 2, pt: 2, pb: 1.5, borderBottom: "1px solid rgba(255,255,255,0.1)"}}>
						<Stack direction="row" alignItems="center" spacing={1} sx={{mb: 1.25}}>
							<SmartToyOutlinedIcon sx={{fontSize: 22}} />
							<Typography sx={{fontWeight: 600, fontSize: "1.05rem", flex: 1}}>
								Morpho Chat
							</Typography>
							<Tooltip title="Refresh">
								<IconButton size="small" onClick={() => this.loadChats()} sx={{color: "#fff"}}>
									<RefreshIcon fontSize="small" />
								</IconButton>
							</Tooltip>
						</Stack>
						<TextField
							size="small"
							fullWidth
							placeholder="Search chats…"
							value={search}
							onChange={this.onSearchChange}
							InputProps={{
								startAdornment: (
									<InputAdornment position="start">
										<SearchIcon sx={{color: "rgba(255,255,255,0.45)", fontSize: 18}} />
									</InputAdornment>
								),
							}}
							sx={{
								"& .MuiOutlinedInput-root": {
									color: "#fff",
									bgcolor: "rgba(255,255,255,0.06)",
									"& fieldset": {borderColor: "rgba(255,255,255,0.15)"},
								},
							}}
						/>
					</Box>
					<Box sx={{flex: 1, overflow: "auto"}}>
						{loading && (
							<Box sx={{display: "flex", justifyContent: "center", py: 3}}>
								<CircularProgress size={22} />
							</Box>
						)}
						{!loading && !chats.length && (
							<Typography sx={{px: 2, py: 3, opacity: 0.65, fontSize: "0.875rem"}}>
								No chats yet. Open a slide and use Morpho Chat in the viewer.
							</Typography>
						)}
						{isSuperuser() && (
							<Typography sx={{px: 2, pt: 1, pb: 0.5, fontSize: "0.68rem", opacity: 0.55}}>
								Admin: archived chats (not permanently deleted) appear below with a badge.
							</Typography>
						)}
						<List dense disablePadding>
							{chats.map((chat) => {
								const active = String(chat.slide_id) === String(selectedSlideId)
									&& (!selectedChatId || String(chat.chat_id || "") === String(selectedChatId || ""));
								const thumb = this.thumbSrc(chat);
								const rowKey = chatRowKey(chat);
								const editing = this.state.editingKey === rowKey;
								return (
									<ListItemButton
										key={rowKey}
										selected={active}
										onClick={() => this.selectChat(chat)}
										sx={{
											alignItems: "flex-start",
											py: 1.25,
											px: 1.5,
											borderBottom: "1px solid rgba(255,255,255,0.06)",
											position: "relative",
											opacity: chat.archived ? 0.85 : 1,
											"&.Mui-selected": {bgcolor: "rgba(158,203,255,0.12)"},
											"&:hover .chat-row-edit, & .chat-row-edit:focus-visible": {opacity: 1},
										}}
									>
										{chat.chat_id && !editing && (
											<Tooltip title="Rename chat">
												<IconButton
													className="chat-row-edit"
													size="small"
													onClick={(e) => {
														e.stopPropagation();
														this.startRename(chat);
													}}
													onMouseDown={(e) => e.stopPropagation()}
													sx={{
														position: "absolute",
														right: 6,
														top: 6,
														zIndex: 1,
														opacity: 0,
														transition: "opacity 120ms",
														color: "#fff",
														bgcolor: "#2a2a33",
														"&:hover": {bgcolor: "#3a3a46"},
													}}
												>
													<EditOutlinedIcon sx={{fontSize: 16}} />
												</IconButton>
											</Tooltip>
										)}
										<Box
											sx={{
												width: 48,
												height: 48,
												borderRadius: 1,
												overflow: "hidden",
												flexShrink: 0,
												mr: 1.25,
												bgcolor: "rgba(255,255,255,0.08)",
												border: "1px solid rgba(255,255,255,0.12)",
											}}
										>
											{thumb ? (
												<img
													src={thumb}
													alt=""
													style={{width: "100%", height: "100%", objectFit: "cover"}}
												/>
											) : (
												<Box sx={{
													width: "100%", height: "100%", display: "flex",
													alignItems: "center", justifyContent: "center", opacity: 0.4,
												}}>
													<SmartToyOutlinedIcon fontSize="small" />
												</Box>
											)}
										</Box>
										<ListItemText
											primary={
												editing ? (
													<TextField
														autoFocus
														fullWidth
														size="small"
														variant="standard"
														value={this.state.editingTitle}
														inputProps={{maxLength: 120, "aria-label": "Chat name"}}
														onChange={(e) => this.setState({editingTitle: e.target.value})}
														onClick={(e) => e.stopPropagation()}
														onMouseDown={(e) => e.stopPropagation()}
														onFocus={(e) => e.target.select()}
														onKeyDown={(e) => {
															e.stopPropagation();
															if (e.key === "Enter") this.commitRename(chat);
															else if (e.key === "Escape") this.cancelRename();
														}}
														onBlur={() => this.commitRename(chat)}
														sx={{
															"& .MuiInputBase-input": {color: "#fff", fontSize: "0.85rem", fontWeight: 600, py: 0.25},
															"& .MuiInput-underline:before": {borderBottomColor: "rgba(255,255,255,0.3)"},
														}}
													/>
												) : (
												<Stack direction="row" justifyContent="space-between" spacing={1} alignItems="center">
													<Typography noWrap sx={{fontSize: "0.85rem", fontWeight: 600}}>
														{chatDisplayTitle(chat)}
														{chat.archived ? (
															<Box
																component="span"
																sx={{
																	ml: 0.75,
																	px: 0.5,
																	py: 0.1,
																	borderRadius: 0.5,
																	fontSize: "0.62rem",
																	fontWeight: 500,
																	bgcolor: "rgba(255,193,7,0.2)",
																	color: "#ffe082",
																	verticalAlign: "middle",
																}}
															>
																Archived
															</Box>
														) : null}
													</Typography>
													<Typography sx={{fontSize: "0.7rem", opacity: 0.55, flexShrink: 0}}>
														{formatWhen(chat.updated_at)}
													</Typography>
												</Stack>
												)
											}
											secondary={
												<Typography
													sx={{
														fontSize: "0.78rem",
														opacity: 0.7,
														mt: 0.25,
														display: "-webkit-box",
														WebkitLineClamp: 2,
														WebkitBoxOrient: "vertical",
														overflow: "hidden",
													}}
												>
													{chat.title && chat.morphle_id ? `${chat.morphle_id} · ` : ""}
													{chat.archived ? "Hidden from users · not permanently deleted · " : ""}
													{chat.preview || `${chat.message_count || 0} messages`}
												</Typography>
											}
										/>
									</ListItemButton>
								);
							})}
						</List>
					</Box>
				</Box>

				{/* Right: full chat */}
				<Box sx={{flex: 1, minWidth: 0, display: "flex", flexDirection: "column", bgcolor: "#14141a"}}>
					{selectedSlideId ? (
						<>
							<Box
								sx={{
									px: 2,
									py: 1.25,
									borderBottom: "1px solid rgba(255,255,255,0.1)",
									display: "flex",
									alignItems: "center",
									gap: 1,
									flexShrink: 0,
								}}
							>
								<Typography noWrap sx={{fontWeight: 600, flex: 1, minWidth: 0}}>
									{chatDisplayTitle(selected || {slide_id: selectedSlideId})}
									{selected?.title && selected?.morphle_id ? (
										<Box component="span" sx={{ml: 1, fontWeight: 400, fontSize: "0.8rem", opacity: 0.55}}>
											{selected.morphle_id}
										</Box>
									) : null}
								</Typography>
								<Typography sx={{fontSize: "0.75rem", opacity: 0.55, mr: 1}}>
									{selected?.archived
										? "Archived (admin) · not permanently deleted"
										: "Your chat only · pathology slide Q&A"}
								</Typography>
								<Tooltip title="Open slide in viewer (new chat panel)">
									<IconButton
										size="small"
										onClick={() => this.openInViewer(selected || {slide_id: selectedSlideId})}
										sx={{color: "#fff"}}
									>
										<OpenInNewIcon fontSize="small" />
									</IconButton>
								</Tooltip>
							</Box>
							<Box sx={{flex: 1, minHeight: 0, p: 1.5}}>
								<AiChatApp
									key={`${selectedSlideId}:${selectedChatId || "active"}`}
									variant="dashboard"
									hideTitle
									slide={{id: selectedSlideId}}
									initialChatId={selectedChatId || undefined}
									chatTitle={selected?.title}
									onChatRenamed={this.onEmbeddedChatRenamed}
									slidemap={null}
								/>
							</Box>
						</>
					) : (
						<Box
							sx={{
								flex: 1,
								display: "flex",
								flexDirection: "column",
								alignItems: "center",
								justifyContent: "center",
								opacity: 0.7,
								px: 4,
								textAlign: "center",
							}}
						>
							<SmartToyOutlinedIcon sx={{fontSize: 48, mb: 1.5, opacity: 0.5}} />
							<Typography sx={{fontWeight: 600, mb: 0.5}}>Select a chat</Typography>
							<Typography sx={{fontSize: "0.875rem", maxWidth: 360}}>
								Your Morpho Chat history across slides. Click an image in a thread to open
								that region in the viewer.
							</Typography>
						</Box>
					)}
				</Box>
			</Box>
		);
	}
}

export default MorphoChatDashboard;
